import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as create$3, d as create$7, F as FormController } from './form.js';
import { A as Api } from './Api.js';
import { C as ComponentErrorSeverity, a as ComponentErrorCodes } from './ComponentError.js';
import './Checkout.js';
import './Pagination.js';
import './Payment.js';
import './Refund.js';
import './Document.js';
import { h as heading2 } from './parts.js';
import './Business.js';
import { B as BusinessFormStep } from './event-types.js';
import { d as defineCustomElement$1 } from './form-control-checkbox2.js';

const businessTermsConditionsSchema = (allowOptionalFields) => {
    const schema = create$3({
        accepted: create$7()
            .oneOf([true], 'You must agree to the terms and conditions to continue')
            .required('You must agree to the terms and conditions to continue')
    });
    const easySchema = create$3({
        accepted: create$7().nullable()
    });
    return allowOptionalFields ? easySchema : schema;
};

const BusinessTermsConditionsFormStep = /*@__PURE__*/ proxyCustomElement(class BusinessTermsConditionsFormStep extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.stepCompleteEvent = createEvent(this, "complete-form-step-event", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.formCompleted = createEvent(this, "formCompleted", 7);
        this.errors = {};
        this.fetchData = async () => {
            this.formLoading.emit(true);
            try {
                const response = await this.api.get({ endpoint: this.businessEndpoint });
                this.acceptedTermsBefore = response.data.terms_conditions_accepted;
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.FETCH_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.sendData = async (onSuccess) => {
            this.formLoading.emit(true);
            try {
                const payload = this.termsPayload;
                const response = await this.api.post({ endpoint: this.termsConditionsEndpoint, body: payload });
                this.handleResponse(response, onSuccess);
            }
            catch (error) {
                this.errorEvent.emit({
                    errorCode: ComponentErrorCodes.POST_ERROR,
                    message: error.message,
                    severity: ComponentErrorSeverity.ERROR,
                    data: error,
                });
            }
            finally {
                this.formLoading.emit(false);
            }
        };
        this.inputHandler = (name, value) => {
            this.acceptedTerms = value;
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
    }
    get businessEndpoint() {
        return `entities/business/${this.businessId}`;
    }
    get termsConditionsEndpoint() {
        return 'entities/terms_and_conditions';
    }
    get formHelperText() {
        return this.acceptedTermsBefore ? 'You have already accepted the terms and conditions.' : null;
    }
    get termsPayload() {
        return {
            business_id: this.businessId,
            accepted: this.formController.values.getValue().accepted,
            user_agent: window.navigator.userAgent
        };
    }
    async componentWillLoad() {
        this.api = Api({ authToken: this.authToken, apiOrigin: "https://wc-proxy.justifi.ai" });
        this.formController = new FormController(businessTermsConditionsSchema(this.allowOptionalFields));
        if (this.businessId && this.authToken) {
            this.fetchData();
        }
    }
    handleResponse(response, onSuccess) {
        if (response.error) {
            this.errorEvent.emit({
                errorCode: ComponentErrorCodes.POST_ERROR,
                message: response.error.message,
                severity: ComponentErrorSeverity.ERROR,
                data: response.error,
            });
        }
        else {
            onSuccess();
        }
        this.stepCompleteEvent.emit({ response: response, formStep: BusinessFormStep.termsAndConditions });
        this.formCompleted.emit();
    }
    async validateAndSubmit({ onSuccess }) {
        if (this.acceptedTermsBefore) {
            this.stepCompleteEvent.emit({ response: null, formStep: BusinessFormStep.termsAndConditions, metadata: 'no data submitted' });
            this.formCompleted.emit();
            onSuccess();
        }
        else {
            this.formController.validateAndSubmit(() => this.sendData(onSuccess));
        }
    }
    ;
    componentDidLoad() {
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    get merchantAgreementLink() {
        return (h("a", { href: "https://justifi.tech/merchant-agreement/", target: "_blank", rel: "noopener noreferrer" }, "merchant agreement"));
    }
    render() {
        return (h("form", { key: '2da7d2ed790cc896168c3034642b5e1c4b5aab5b' }, h("fieldset", { key: '1fd89c9ef9d1f65647280d8b7d80625ee6fcde3a' }, h("legend", { key: 'c8d4b44f530bf4b702523ebd10b2abef4321c1e5', part: heading2 }, "Terms and Conditions"), h("hr", { key: '5b16410dea6ee295e9546bb7d9f69c47b5a3a2a0' }), h("div", { key: '0863be82b21e863fbd59eadcf6eb6bf214f7be5c' }, h("p", { key: '9cc14fe90dafba81b5f979b6cd36bfb820c84d5e' }, "Please read and accept the ", this.merchantAgreementLink, " to submit your provisioning request.")), h("br", { key: '357aa6a506fe3c4fdb2d2cdd5250096fc6707bad' }), h("div", { key: 'f9196a0f47187d263b1eea9157307e2a0bd58eab', class: "row-gy-3" }, h("div", { key: 'd753c0ea76488813acc49034e433f82ac6aea8c2', class: "col-12" }, h("form-control-checkbox", { key: 'e2fd2f318fd357959bb0768008f8c9f0242f7fe6', name: "accepted", label: "I agree to the terms and conditions", inputHandler: this.inputHandler, errorText: this.errors.accepted, disabled: this.acceptedTermsBefore, helpText: this.formHelperText, checked: this.acceptedTerms }))))));
    }
}, [0, "justifi-business-terms-conditions-form-step", {
        "authToken": [1, "auth-token"],
        "businessId": [1, "business-id"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "acceptedTermsBefore": [32],
        "acceptedTerms": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-terms-conditions-form-step", "form-control-checkbox"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-terms-conditions-form-step":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessTermsConditionsFormStep);
            }
            break;
        case "form-control-checkbox":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessTermsConditionsFormStep as B, defineCustomElement as d };
